import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Users, DollarSign, TrendingUp, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FinanceNav } from '@/components/finance';
import { useSellerPerformance } from '@/hooks/finance/useSellerPerformance';

const formatCurrency = (value: number | null, currency: string = 'USD') => {
  if (value === null) return '-';
  return new Intl.NumberFormat(currency === 'BRL' ? 'pt-BR' : 'en-US', {
    style: 'currency',
    currency,
  }).format(value);
};

const formatPercent = (value: number | null) => {
  if (value === null) return '-';
  return `${value.toFixed(1)}%`;
};

export default function SellerPerformanceReport() {
  const [businessUnit, setBusinessUnit] = useState<string>('all');
  const [brand, setBrand] = useState<string>('all');

  const { data: sellers, isLoading } = useSellerPerformance(
    businessUnit === 'all' ? undefined : businessUnit,
    brand === 'all' ? undefined : brand
  );

  // Calculate totals
  const totals = sellers?.reduce((acc, seller) => ({
    totalDeals: acc.totalDeals + (seller.total_deals || 0),
    totalVendido: acc.totalVendido + (seller.valor_total_vendido || 0),
    totalRecebido: acc.totalRecebido + (seller.valor_recebido || 0),
    totalCreditos: acc.totalCreditos + (seller.valor_creditos_upsell || 0),
    totalLiquido: acc.totalLiquido + (seller.valor_liquido_vendido || 0),
  }), { totalDeals: 0, totalVendido: 0, totalRecebido: 0, totalCreditos: 0, totalLiquido: 0 });

  const avgRecebimento = sellers?.length 
    ? sellers.reduce((sum, s) => sum + (s.taxa_recebimento || 0), 0) / sellers.filter(s => s.taxa_recebimento !== null).length
    : 0;

  const avgDso = sellers?.length
    ? sellers.reduce((sum, s) => sum + (s.dso_medio || 0), 0) / sellers.filter(s => s.dso_medio !== null).length
    : 0;

  const currency = businessUnit === 'br' ? 'BRL' : 'USD';

  return (
    <div className="space-y-6">
      <FinanceNav />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Performance por Vendedor</h1>

        <div className="flex gap-2">
          <Select value={businessUnit} onValueChange={setBusinessUnit}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Business Unit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="us">US</SelectItem>
              <SelectItem value="br">BR</SelectItem>
            </SelectContent>
          </Select>

          <Select value={brand} onValueChange={setBrand}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Brand" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="divus">Divus</SelectItem>
              <SelectItem value="blueviza">BlueViza</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-[120px]" />
            ))}
          </div>
          <Skeleton className="h-[400px]" />
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Vendedores Ativos</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{sellers?.length || 0}</div>
                <p className="text-xs text-muted-foreground">
                  {totals?.totalDeals || 0} deals no total
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Vendido (Bruto)</CardTitle>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(totals?.totalVendido || 0, currency)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Valor total de contratos
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Vendas Líquidas</CardTitle>
                <DollarSign className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">
                  {formatCurrency(totals?.totalLiquido || 0, currency)}
                </div>
                <p className="text-xs text-muted-foreground">
                  {totals?.totalCreditos ? `(-${formatCurrency(totals.totalCreditos, currency)} créditos upsell)` : 'Descontando créditos de upsell'}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Taxa Média Recebimento</CardTitle>
                <TrendingUp className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">
                  {formatPercent(avgRecebimento)}
                </div>
                <p className="text-xs text-muted-foreground">
                  Média entre vendedores
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">DSO Médio</CardTitle>
                <Clock className="h-4 w-4 text-orange-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600">
                  {avgDso ? `${avgDso.toFixed(0)} dias` : '-'}
                </div>
                <p className="text-xs text-muted-foreground">
                  Dias para receber
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Sellers Table */}
          <Card>
            <CardHeader>
              <CardTitle>Desempenho Individual</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vendedor</TableHead>
                    <TableHead className="text-center">Deals</TableHead>
                    <TableHead className="text-right">Vendido (Bruto)</TableHead>
                    <TableHead className="text-right">Créditos Upsell</TableHead>
                    <TableHead className="text-right">Vendas Líquidas</TableHead>
                    <TableHead className="text-right">Recebido</TableHead>
                    <TableHead className="text-center">% Recebimento</TableHead>
                    <TableHead className="text-center">% Inadimplência</TableHead>
                    <TableHead className="text-center">DSO</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {!sellers || sellers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                        Nenhum vendedor encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    sellers.map((seller) => {
                      const hasHighDefault = (seller.taxa_inadimplencia || 0) > 50;
                      const hasLowDso = (seller.dso_medio || 0) < 30 && seller.dso_medio !== null;
                      
                      return (
                        <TableRow key={`${seller.seller_id}-${seller.business_unit}`}>
                          <TableCell className="font-medium">
                            <div className="flex flex-col">
                              <span>{seller.seller_name}</span>
                              <span className="text-xs text-muted-foreground">
                                {seller.business_unit?.toUpperCase() || 'N/A'} • {seller.brand}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant="secondary">{seller.total_deals}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(seller.valor_total_vendido, currency)}
                          </TableCell>
                          <TableCell className="text-right text-orange-600">
                            {seller.valor_creditos_upsell && seller.valor_creditos_upsell > 0 
                              ? `-${formatCurrency(seller.valor_creditos_upsell, currency)}`
                              : '-'
                            }
                          </TableCell>
                          <TableCell className="text-right font-medium text-green-600">
                            {formatCurrency(seller.valor_liquido_vendido, currency)}
                          </TableCell>
                          <TableCell className="text-right text-blue-600">
                            {formatCurrency(seller.valor_recebido, currency)}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant={seller.taxa_recebimento && seller.taxa_recebimento >= 80 ? "default" : "secondary"}>
                              {formatPercent(seller.taxa_recebimento)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge 
                              variant={hasHighDefault ? "destructive" : "outline"}
                              className={cn(
                                hasHighDefault && "bg-red-100 text-red-800 border-red-300"
                              )}
                            >
                              {formatPercent(seller.taxa_inadimplencia)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge
                              variant={hasLowDso ? "default" : "outline"}
                              className={cn(
                                hasLowDso && "bg-green-100 text-green-800 border-green-300"
                              )}
                            >
                              {seller.dso_medio ? `${seller.dso_medio.toFixed(0)}d` : '-'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
